import { CircleAlert, CircleCheck, Info, TriangleAlert, X, type LucideIcon } from "lucide-react";
import type { ReactNode, Ref } from "react";
import { cn } from "@/lib/utils";

type Tone = "info" | "success" | "warning" | "danger";

const tones: Record<Tone, { icon: LucideIcon; className: string; role: "status" | "alert" }> = {
  info: { icon: Info, className: "text-info", role: "status" },
  success: { icon: CircleCheck, className: "text-success", role: "status" },
  warning: { icon: TriangleAlert, className: "text-warning", role: "alert" },
  danger: { icon: CircleAlert, className: "text-danger", role: "alert" },
};

/** Without a title, the children are the message itself, in the title's colour. */
export function Alert({
  tone = "info",
  title,
  children,
  action,
  onDismiss,
  className,
  role: roleOverride,
  ...rest
}: {
  tone?: Tone;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
  onDismiss?: () => void;
  className?: string;
  /** A message already on the page when it loads is not news: pass role="note" (or none) to keep it out of the live region. */
  role?: string;
  ref?: Ref<HTMLDivElement>;
  id?: string;
  tabIndex?: number;
} & { [data: `data-${string}`]: string | undefined }) {
  const { icon: Icon, className: toneClass, role } = tones[tone];
  return (
    <div
      {...rest}
      role={roleOverride ?? role}
      data-slot="alert"
      data-tone={tone}
      className={cn("flex items-start gap-3 rounded-panel border border-line-subtle bg-surface p-4 shadow-resting", className)}
    >
      <Icon aria-hidden className={cn("mt-0.5 size-5 shrink-0", toneClass)} strokeWidth={1.75} />
      <div className="grid min-w-0 flex-1 gap-1">
        {title ? <p className="text-sm font-medium text-foreground">{title}</p> : null}
        {children ? <div className={cn("text-sm", title ? "text-secondary" : "text-foreground")}>{children}</div> : null}
        {action ? <div className="mt-2 flex flex-wrap gap-2">{action}</div> : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="-mt-2.5 -mr-2.5 grid size-11 shrink-0 place-items-center rounded-pill text-secondary can-hover:hover:bg-surface-muted"
        >
          <X aria-hidden className="size-4" strokeWidth={1.75} />
        </button>
      ) : null}
    </div>
  );
}
